import React, { useEffect, useMemo, useState } from "react";
import { calls as callsApi, hotels, shoppers as shoppersApi } from "../api/endpoints";
import { DataTableShell, EmptyState, MetricStrip, PageHeader, SectionCard } from "../components/Workspace";
import type { CallImportSummary, CallType, HotelProperty, ServiceLevel, User } from "../types";

const EXPECTED_COLUMNS = [
  "HotelCode",
  "HotelName",
  "SalesStaffName",
  "SalesStaffEmail",
  "ShoppersEmail",
  "CallType",
  "ServiceLevel",
  "Scenario",
  "ScheduledMonth",
  "ScheduledDate",
  "Earliest",
  "HMC",
  "ContractYear",
  "OrderID",
  "InvoiceID",
  "Language",
];

function statusTone(status: string) {
  if (status === "imported") return "tone-success";
  if (status === "ready") return "tone-brand";
  return "tone-danger";
}

function StatusPill({ status }: { status: string }) {
  return (
    <span className={`badge-pill ${statusTone(status)}`.trim()}>
      {status === "ready" ? "Ready" : status === "imported" ? "Imported" : "Needs review"}
    </span>
  );
}

function serviceLevelLabel(serviceLevel: ServiceLevel) {
  return [serviceLevel.name, serviceLevel.description || serviceLevel.abbreviation]
    .filter(Boolean)
    .join(" - ");
}

export default function ImportedCallsPage() {
  const [file, setFile] = useState<File | null>(null);
  const [summary, setSummary] = useState<CallImportSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [manualSaving, setManualSaving] = useState(false);
  const [properties, setProperties] = useState<HotelProperty[]>([]);
  const [callTypes, setCallTypes] = useState<CallType[]>([]);
  const [serviceLevels, setServiceLevels] = useState<ServiceLevel[]>([]);
  const [shoppers, setShoppers] = useState<User[]>([]);
  const [manualForm, setManualForm] = useState({
    property_id: 0,
    sales_staff_name: "",
    sales_staff_email: "",
    shopper_email: "",
    call_type: "",
    service_level: "",
    scenario: "",
    scheduled_date: "",
    earliest: "",
    invoice_id: "",
    order_id: "",
    language: "",
  });
  const [notice, setNotice] = useState<{ tone: "success" | "danger"; message: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      hotels.properties.list("limit=5000"),
      callsApi.types(),
      callsApi.serviceLevels(),
      shoppersApi.list("limit=5000"),
    ]).then(([propertyRows, typeRows, serviceRows, shopperRows]) => {
      if (cancelled) return;
      setProperties(propertyRows.items);
      setCallTypes(typeRows);
      setServiceLevels(serviceRows);
      setShoppers(shopperRows.items);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const metricItems = useMemo(() => {
    if (!summary) {
      return [
        { label: "Rows", value: 0, detail: "Upload a file to validate", tone: "neutral" as const },
        { label: "Ready", value: 0, detail: "Rows that can import cleanly", tone: "brand" as const },
        { label: "Warnings", value: 0, detail: "Rows with non-blocking notes", tone: "warning" as const },
        { label: "Issues", value: 0, detail: "Rows that need changes", tone: "danger" as const },
      ];
    }
    return [
      { label: "Rows", value: summary.total_rows, detail: summary.file_name, tone: "neutral" as const },
      { label: "Ready", value: summary.ready_rows, detail: "Can be imported now", tone: "brand" as const },
      { label: "Warnings", value: summary.warning_rows, detail: "Will still import", tone: "warning" as const },
      {
        label: importing || summary.imported_rows > 0 ? "Imported" : "Issues",
        value: importing || summary.imported_rows > 0 ? summary.imported_rows : summary.invalid_rows,
        detail: importing || summary.imported_rows > 0 ? "Rows created in calls" : "Rows blocked by validation",
        tone: importing || summary.imported_rows > 0 ? ("success" as const) : ("danger" as const),
      },
    ];
  }, [summary, importing]);

  const canImport = Boolean(file && summary && summary.ready_rows > 0 && !loading && !importing);

  async function handlePreview() {
    if (!file) {
      setNotice({ tone: "danger", message: "Choose a .csv, .xls, or .xlsx file first." });
      return;
    }
    setLoading(true);
    setNotice(null);
    try {
      const result = await callsApi.importPreview(file);
      setSummary(result);
      setNotice({ tone: "success", message: result.message });
    } catch (error) {
      setSummary(null);
      setNotice({ tone: "danger", message: error instanceof Error ? error.message : "Preview failed." });
    } finally {
      setLoading(false);
    }
  }

  async function handleImport() {
    if (!file) return;
    setImporting(true);
    setNotice(null);
    try {
      const result = await callsApi.importCommit(file);
      setSummary(result);
      setNotice({
        tone: result.failed_rows > 0 ? "danger" : "success",
        message: result.message,
      });
    } catch (error) {
      setNotice({ tone: "danger", message: error instanceof Error ? error.message : "Import failed." });
    } finally {
      setImporting(false);
    }
  }

  function csvCell(value: string | number | null | undefined) {
    const text = String(value ?? "");
    return `"${text.replace(/"/g, '""')}"`;
  }

  async function handleManualSubmit(event: React.FormEvent) {
    event.preventDefault();
    const property = properties.find((item) => item.id === manualForm.property_id);
    if (!property) {
      setNotice({ tone: "danger", message: "Choose a hotel property before saving the manual call." });
      return;
    }
    setManualSaving(true);
    setNotice(null);
    try {
      const header = EXPECTED_COLUMNS.join(",");
      const row = [
        property.hotel_code,
        property.name,
        manualForm.sales_staff_name,
        manualForm.sales_staff_email,
        manualForm.shopper_email,
        manualForm.call_type,
        manualForm.service_level,
        manualForm.scenario,
        manualForm.scheduled_date ? String(new Date(`${manualForm.scheduled_date}T00:00:00`).getMonth() + 1) : "",
        manualForm.scheduled_date,
        manualForm.earliest,
        property.management_company?.name || "",
        manualForm.scheduled_date ? String(new Date(`${manualForm.scheduled_date}T00:00:00`).getFullYear()) : "",
        manualForm.order_id,
        manualForm.invoice_id,
        manualForm.language,
      ].map(csvCell).join(",");
      const manualFile = new File([`${header}\n${row}\n`], "manual-shop-call.csv", { type: "text/csv" });
      const result = await callsApi.importCommit(manualFile);
      setSummary(result);
      if (result.failed_rows > 0) {
        setNotice({ tone: "danger", message: "The manual call was checked, but it needs review before it can be saved." });
      } else {
        setNotice({ tone: "success", message: "Manual shop call saved." });
        setManualForm({
          property_id: 0,
          sales_staff_name: "",
          sales_staff_email: "",
          shopper_email: "",
          call_type: "",
          service_level: "",
          scenario: "",
          scheduled_date: "",
          earliest: "",
          invoice_id: "",
          order_id: "",
          language: "",
        });
      }
    } catch (error) {
      setNotice({ tone: "danger", message: error instanceof Error ? error.message : "Manual call save failed." });
    } finally {
      setManualSaving(false);
    }
  }

  return (
    <div className="space-y-5">
      <PageHeader
        eyebrow="Imported Calls"
        title="Import Call Details"
        description="Upload a call-details spreadsheet, validate every row, and import only the rows that resolve cleanly."
        compact
      />

      <MetricStrip items={metricItems} />

      <SectionCard
        title="Enter One Shop Call"
        subtitle="Add a single shop call here when there is no spreadsheet to upload."
      >
        <form onSubmit={handleManualSubmit} className="grid gap-4 lg:grid-cols-3">
          <div className="lg:col-span-3">
            <label className="label-caps mb-1.5 block">Hotel Property</label>
            <select
              value={manualForm.property_id}
              onChange={(event) => {
                const propertyId = Number(event.target.value);
                const property = properties.find((item) => item.id === propertyId);
                setManualForm({
                  ...manualForm,
                  property_id: propertyId,
                  sales_staff_name: manualForm.sales_staff_name || property?.dos_name || "",
                  sales_staff_email: manualForm.sales_staff_email || property?.dos_email || "",
                });
              }}
              className="select-shell w-full"
              required
            >
              <option value={0}>Select hotel</option>
              {properties.map((property) => (
                <option key={property.id} value={property.id}>
                  {property.hotel_code} - {property.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label-caps mb-1.5 block">Sales Staff Name</label>
            <input
              value={manualForm.sales_staff_name}
              onChange={(event) => setManualForm({ ...manualForm, sales_staff_name: event.target.value })}
              className="input-shell w-full"
              placeholder="Salesperson name"
            />
          </div>
          <div>
            <label className="label-caps mb-1.5 block">Sales Staff Email</label>
            <input
              type="email"
              value={manualForm.sales_staff_email}
              onChange={(event) => setManualForm({ ...manualForm, sales_staff_email: event.target.value })}
              className="input-shell w-full"
              placeholder="sales@hotel.com"
            />
          </div>
          <div>
            <label className="label-caps mb-1.5 block">Shopper / Coach</label>
            <select
              value={manualForm.shopper_email}
              onChange={(event) => setManualForm({ ...manualForm, shopper_email: event.target.value })}
              className="select-shell w-full"
              required
            >
              <option value="">Select shopper</option>
              {shoppers.map((shopper) => (
                <option key={shopper.id} value={shopper.email}>
                  {`${shopper.first_name} ${shopper.last_name}`.trim() || shopper.email} - {shopper.email}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label-caps mb-1.5 block">Scheduled Date</label>
            <input
              type="date"
              value={manualForm.scheduled_date}
              onChange={(event) => setManualForm({ ...manualForm, scheduled_date: event.target.value })}
              className="input-shell w-full"
              required
            />
          </div>
          <div>
            <label className="label-caps mb-1.5 block">Call Type</label>
            <select
              value={manualForm.call_type}
              onChange={(event) => setManualForm({ ...manualForm, call_type: event.target.value })}
              className="select-shell w-full"
              required
            >
              <option value="">Select type</option>
              {callTypes.map((callType) => (
                <option key={callType.id} value={callType.name}>{callType.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label-caps mb-1.5 block">Service Level</label>
            <select
              value={manualForm.service_level}
              onChange={(event) => setManualForm({ ...manualForm, service_level: event.target.value })}
              className="select-shell w-full"
              required
            >
              <option value="">Select service</option>
              {serviceLevels.map((serviceLevel) => {
                const label = serviceLevelLabel(serviceLevel);
                return <option key={serviceLevel.id} value={label}>{label}</option>;
              })}
            </select>
          </div>
          <div>
            <label className="label-caps mb-1.5 block">Earliest Date</label>
            <input
              type="date"
              value={manualForm.earliest}
              onChange={(event) => setManualForm({ ...manualForm, earliest: event.target.value })}
              className="input-shell w-full"
            />
          </div>
          <div>
            <label className="label-caps mb-1.5 block">Invoice ID</label>
            <input
              value={manualForm.invoice_id}
              onChange={(event) => setManualForm({ ...manualForm, invoice_id: event.target.value })}
              className="input-shell w-full"
              placeholder="Optional"
            />
          </div>
          <div>
            <label className="label-caps mb-1.5 block">Order ID</label>
            <input
              value={manualForm.order_id}
              onChange={(event) => setManualForm({ ...manualForm, order_id: event.target.value })}
              className="input-shell w-full"
              placeholder="Optional"
            />
          </div>
          <div>
            <label className="label-caps mb-1.5 block">Language</label>
            <input
              value={manualForm.language}
              onChange={(event) => setManualForm({ ...manualForm, language: event.target.value })}
              className="input-shell w-full"
              placeholder="Optional"
            />
          </div>
          <div className="lg:col-span-3">
            <label className="label-caps mb-1.5 block">Scenario</label>
            <textarea
              value={manualForm.scenario}
              onChange={(event) => setManualForm({ ...manualForm, scenario: event.target.value })}
              className="input-shell min-h-[96px] w-full"
              placeholder="Add the shop call scenario."
            />
          </div>
          <div className="lg:col-span-3 flex justify-end">
            <button type="submit" className="button-primary" disabled={manualSaving}>
              {manualSaving ? "Saving..." : "Save one shop call"}
            </button>
          </div>
        </form>
      </SectionCard>

      <SectionCard
        title="Upload File"
        subtitle="The importer accepts .csv, .xls, and .xlsx files. Review the validation table before committing any rows."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={handlePreview} className="button-secondary" disabled={!file || loading || importing}>
              {loading ? "Validating..." : "Validate file"}
            </button>
            <button type="button" onClick={handleImport} className="button-primary" disabled={!canImport}>
              {importing ? "Importing..." : "Import ready rows"}
            </button>
          </div>
        }
      >
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1.2fr),minmax(0,0.8fr)]">
          <div className="space-y-3">
            <input
              type="file"
              accept=".xls,.xlsx,.csv"
              onChange={(event) => {
                setFile(event.target.files?.[0] || null);
                setSummary(null);
                setNotice(null);
              }}
              className="input-shell !py-3"
            />
            <div className="text-sm text-slate-500">
              Use the legacy-style columns and keep the header row intact. Existing matching calls are blocked as duplicates.
            </div>
            {notice ? (
              <div
                className={`rounded-2xl border px-4 py-3 text-sm ${
                  notice.tone === "success"
                    ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                    : "border-rose-200 bg-rose-50 text-rose-800"
                }`.trim()}
              >
                {notice.message}
              </div>
            ) : null}
          </div>

          <div className="rounded-[28px] border border-slate-200 bg-slate-50/70 p-4">
            <div className="text-sm font-semibold text-slate-900">Expected Columns</div>
            <div className="mt-2 flex flex-wrap gap-2">
              {EXPECTED_COLUMNS.map((column) => (
                <span key={column} className="badge-pill tone-neutral">
                  {column}
                </span>
              ))}
            </div>
          </div>
        </div>
      </SectionCard>

      <DataTableShell
        title="Validation Results"
        subtitle="Rows marked ready can import now. Errors are blocking. Warnings are informational."
      >
        {summary && summary.rows.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="table-modern">
              <thead>
                <tr>
                  <th>Row</th>
                  <th>Status</th>
                  <th>Hotel</th>
                  <th>Sales Staff</th>
                  <th>Shopper</th>
                  <th>Type / Service</th>
                  <th>Scheduled</th>
                  <th>Messages</th>
                </tr>
              </thead>
              <tbody>
                {summary.rows.map((row) => (
                  <tr key={row.row_number}>
                    <td className="font-mono text-sm font-semibold text-slate-900">{row.row_number}</td>
                    <td>
                      <div className="space-y-2">
                        <StatusPill status={row.status} />
                        {row.imported_call_id ? (
                          <div className="text-xs text-emerald-700">Call #{row.imported_call_id}</div>
                        ) : null}
                        {row.duplicate_existing_call_id ? (
                          <div className="text-xs text-amber-700">Duplicate #{row.duplicate_existing_call_id}</div>
                        ) : null}
                      </div>
                    </td>
                    <td>
                      <div className="font-semibold text-slate-900">{row.hotel_code || "No code"}</div>
                      <div className="mt-1 text-sm text-slate-600">{row.resolved_property_name || row.hotel_name || "Unresolved hotel"}</div>
                      {row.resolved_hmc_name ? (
                        <div className="mt-1 text-xs uppercase tracking-[0.18em] text-slate-500">{row.resolved_hmc_name}</div>
                      ) : null}
                    </td>
                    <td>
                      <div className="font-medium text-slate-900">{row.resolved_sales_staff_name || "Unassigned"}</div>
                      <div className="mt-1 text-sm text-slate-600">{row.sales_staff_email || "No email"}</div>
                    </td>
                    <td>
                      <div className="font-medium text-slate-900">{row.resolved_shopper_name || "Unassigned"}</div>
                      <div className="mt-1 text-sm text-slate-600">{row.shopper_email || "No email"}</div>
                    </td>
                    <td>
                      <div className="font-medium text-slate-900">{row.call_type || "Missing type"}</div>
                      <div className="mt-1 text-sm text-slate-600">{row.service_level || "Missing service level"}</div>
                      {row.resolved_language_name ? (
                        <div className="mt-1 text-xs text-slate-500">Language: {row.resolved_language_name}</div>
                      ) : null}
                    </td>
                    <td>
                      <div className="font-medium text-slate-900">{row.scheduled_date || "Missing date"}</div>
                      {row.scenario ? <div className="mt-1 text-sm text-slate-600">{row.scenario}</div> : null}
                      {row.resolved_order_id ? (
                        <div className="mt-1 text-xs text-slate-500">Order #{row.resolved_order_id}</div>
                      ) : null}
                    </td>
                    <td>
                      <div className="space-y-2">
                        {row.errors.map((message) => (
                          <div key={`error-${row.row_number}-${message}`} className="rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-800">
                            {message}
                          </div>
                        ))}
                        {row.warnings.map((message) => (
                          <div key={`warning-${row.row_number}-${message}`} className="rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-800">
                            {message}
                          </div>
                        ))}
                        {row.errors.length === 0 && row.warnings.length === 0 ? (
                          <div className="text-sm text-slate-500">No issues.</div>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState
            title="No validation yet"
            message="Upload a call-details file and run validation to see row-level feedback before importing."
          />
        )}
      </DataTableShell>
    </div>
  );
}
